<template>
  <div :class="['LocationCover', { 'image-loaded': isImageLoaded, 'is-colorful': isColorful }]">
    <img v-if="image" class="picture" :src="image" loading="lazy" @load="handleImageLoaded" />
  </div>
</template>

<script lang="ts" setup>
import type { SegmentLocation } from '@/core/data/spark/spark.interface';
import { gambits } from '@/core/gambits';
import { getBlobImage } from '@/views/main/sparks/utils/image-blob';
import { isAfter, subWeeks } from 'date-fns';
import { computed, ref, watch } from 'vue';

interface LocationCoverProps {
  location: SegmentLocation;
  isColorful?: boolean;
}

const props = defineProps<LocationCoverProps>();

const isImageLoaded = ref<boolean>(false);
const imageHash = ref<string>(props.location.coverBlobHash || props.location.mapBlobHash || '');

const image = computed(() => {
  if (imageHash.value) {
    return getBlobImage(imageHash.value, 343);
  }

  return null;
});

watch(
  () => props.location,
  async (newLocation, oldLocation) => {
    if (
      newLocation?.coverUpdatedAt === oldLocation?.coverUpdatedAt &&
      newLocation?.coverBlobHash === oldLocation?.coverBlobHash
    ) {
      return;
    }

    if (newLocation?.coverBlobHash) {
      imageHash.value = newLocation.coverBlobHash;
      return;
    }

    imageHash.value = '';

    const aWeekAgo = subWeeks(new Date(), 1);
    const coverUpdated = new Date(newLocation?.coverUpdatedAt || 1);
    if (isAfter(coverUpdated, aWeekAgo)) {
      return;
    }

    const cover = await gambits.locationService.getLocationCover(newLocation.id);

    if (cover?.data) {
      imageHash.value = cover.data;
    } else {
      imageHash.value = '';
    }
  },
  { immediate: true },
);

const handleImageLoaded = () => {
  isImageLoaded.value = true;
};
</script>

<style lang="scss">
.LocationCover {
  width: 100%;
  height: 100%;
  opacity: 0.85;
  transition: opacity var(--transition);
}

.LocationCover.is-colorful {
  opacity: 0.95;
}

.LocationCover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0;
  filter: saturate(0);
}

.LocationCover.is-colorful img {
  filter: none;
}

.LocationCover.image-loaded img {
  opacity: 1;
  transition: opacity var(--transition);
}
</style>
